import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../../components/base/base.component';
import { SessionService } from '../../_services/session/session.service';
import { PatientsService } from '../../_services/patients/patients.service';
import { ErrorsService } from '../../_services/errors/errors.service';
import { AppointmentService } from '../../_services/appointments/appointment.service';
import IPatient from '../../_interfaces/IPatients';
import IAppointment from '../../_interfaces/IAppointment';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { TableModule, Table } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule, Menu } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';

// Components
import { PatientLinkComponent } from '../../components/misc/patient-link/patient-link.component';
import { FormPatientComponent } from '../../components/forms/form-patient/form-patient.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  standalone: true,
  selector: 'app-patients',
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    TableModule,
    InputTextModule,
    CheckboxModule,
    TooltipModule,
    MenuModule,
    SkeletonModule,
    PatientLinkComponent,
  ],
  providers: [DialogService],
})
export class PatientsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('dt') dt: Table;
  @ViewChild('menu') menu: Menu;

  // Data
  public patients: IPatient[] = [];
  public filteredPatients: IPatient[] = [];
  public loading: boolean = true;

  // Filters
  public searchText: string = '';
  public filterIncomplete: boolean = false;

  // Mobile
  public isMobile: boolean = false;
  public showMobileFilters: boolean = false;
  public currentMobilePage: number = 1;
  public mobilePageSize: number = 20;

  // Pagination
  public tableFirst: number = 0;
  public tableRows: number = 20;

  // Menu
  public menuOptions: any[] = [];

  private resizeHandler = () => this.checkMobile();

  constructor(
    public session: SessionService,
    private patientsService: PatientsService,
    private appointmentsService: AppointmentService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private errorsService: ErrorsService,
    private dialogService: DialogService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.hydrateStateFromQueryParams();
    this.checkMobile();
    window.addEventListener('resize', this.resizeHandler);
    this.loadPatients();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    window.removeEventListener('resize', this.resizeHandler);
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  // =========================================================================
  // DATA LOADING
  // =========================================================================

  loadPatients(): void {
    this.loading = true;

    if (this.site) {
      this.patientsService
        .getBySite(this.customers_id, this.site.id)
        .subscribe({
          next: (patients: IPatient[]) => {
            this.patients = patients.map((p) => ({
              ...p,
              fullName:
                `${p.name?.trim() || ''} ${(p.surname || '').trim()}`.trim(),
            }));
            this.applyFilters();
            this.syncQueryParams();
            this.loading = false;
          },
          error: (error) => {
            this.showError(error);
            this.loading = false;
          },
        });
    } else {
      this.patientsService.getAll(this.customers_id).subscribe({
        next: (patients: IPatient[]) => {
          this.patients = patients.map((p) => ({
            ...p,
            fullName:
              `${p.name?.trim() || ''} ${(p.surname || '').trim()}`.trim(),
          }));
          this.applyFilters();
          this.syncQueryParams();
          this.loading = false;
        },
        error: (error) => {
          this.showError(error);
          this.loading = false;
        },
      });
    }
  }

  // =========================================================================
  // FILTERING
  // =========================================================================

  private readPositiveInteger(value: string | null, fallback: number): number {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  private hydrateStateFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const page = this.readPositiveInteger(params.get('page'), 1);
    const rows = this.readPositiveInteger(params.get('rows'), 20);

    this.searchText = params.get('q') || '';
    this.filterIncomplete = params.get('incomplete') === 'true';
    this.tableRows = rows;
    this.tableFirst = (page - 1) * rows;
    this.mobilePageSize = rows;
    this.currentMobilePage = page;
  }

  private getCurrentPage(): number {
    return Math.floor(this.tableFirst / this.tableRows) + 1;
  }

  private syncQueryParams(): void {
    const page = this.isMobile ? this.currentMobilePage : this.getCurrentPage();
    const rows = this.isMobile ? this.mobilePageSize : this.tableRows;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: this.searchText || null,
        incomplete: this.filterIncomplete ? 'true' : null,
        page: page > 1 ? page : null,
        rows: rows !== 20 ? rows : null,
      },
      replaceUrl: true,
    });
  }

  private clampPaginationToFilteredResults(): void {
    const rows = this.tableRows || 20;
    const totalPages = Math.max(1, Math.ceil(this.filteredPatients.length / rows));
    const currentPage = Math.min(this.getCurrentPage(), totalPages);

    this.tableFirst = (currentPage - 1) * rows;
    this.currentMobilePage = Math.min(this.currentMobilePage, this.totalMobilePages || 1);
  }

  private normalizeSearchValue(value: unknown): string {
    return `${value || ''}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  applyFilters(): void {
    let result = [...this.patients];

    // Filter incomplete
    if (this.filterIncomplete) {
      result = result.filter(
        (p) => !this.patientsService.isPatientCompleted(p),
      );
    }

    // Text search
    if (this.searchText) {
      const term = this.normalizeSearchValue(this.searchText);
      result = result.filter(
        (p) =>
          this.normalizeSearchValue(p.fullName).includes(term) ||
          this.normalizeSearchValue(p.email).includes(term) ||
          this.normalizeSearchValue(p.phone).includes(term),
      );
    }

    // Sort alphabetically by fullName (consistent with desktop table default)
    result.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));

    this.filteredPatients = result;
    this.clampPaginationToFilteredResults();
  }

  onSearchChange(): void {
    this.resetPagination();
    this.applyFilters();
    this.syncQueryParams();
  }

  onFilterChange(): void {
    this.resetPagination();
    this.applyFilters();
    this.syncQueryParams();
  }

  clearSearch(): void {
    this.searchText = '';
    this.onSearchChange();
  }

  toggleMobileFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  get filteredCount(): number {
    return this.filteredPatients.length;
  }

  // =========================================================================
  // MOBILE PAGINATION
  // =========================================================================

  get totalMobilePages(): number {
    return Math.ceil(this.filteredPatients.length / this.mobilePageSize);
  }

  get paginatedPatients(): IPatient[] {
    const start = (this.currentMobilePage - 1) * this.mobilePageSize;
    return this.filteredPatients.slice(start, start + this.mobilePageSize);
  }

  goToMobilePage(page: number): void {
    this.currentMobilePage = page;
    this.tableFirst = (page - 1) * this.tableRows;
    this.syncQueryParams();
  }

  onTablePage(event: { first?: number; rows?: number }): void {
    this.tableFirst = event.first || 0;
    this.tableRows = event.rows || 20;
    this.mobilePageSize = this.tableRows;
    this.currentMobilePage = this.getCurrentPage();
    this.syncQueryParams();
  }

  private resetPagination(): void {
    this.tableFirst = 0;
    this.currentMobilePage = 1;
  }

  // =========================================================================
  // ACTIONS
  // =========================================================================

  goToRecord(patient: IPatient): void {
    this.router.navigate([
      `/${this.customer.identifier}/patients/${patient.id}/details`,
    ]);
  }

  editPatient(patient: IPatient): void {
    const ref = this.dialogService.open(FormPatientComponent, {
      closable: true,
      modal: true,
      inputValues: {
        payload: { ...patient },
        exclude: ['site'],
        onUpdate: () => {
          ref.close();
          this.loadPatients();
        },
      },
      header: this.translate.instant('ADMIN.PATIENTS.DIALOG.EDIT.TITLE', {
        patient: patient?.fullName || patient?.name || '-',
      }),
      width: '90%',
      styleClass: 'vc-dialog-lg',
    });
  }

  openCreatePatient(): void {
    const hasContextSite = !!this.site?.id;
    const inputValues: any = {
      onCreate: () => {
        ref.close();
        this.loadPatients();
      },
    };

    if (hasContextSite) {
      inputValues.exclude = ['site'];
      inputValues.affectedSite = this.site;
    }

    const ref = this.dialogService.open(FormPatientComponent, {
      closable: true,
      modal: true,
      header: this.translate.instant('ADMIN.PATIENTS.ADD'),
      width: '90%',
      styleClass: 'vc-dialog-lg',
      inputValues,
    });
  }

  confirmRemove(patient: IPatient): void {
    this.appointmentsService
      .getByPatient(this.customers_id, patient.id as number)
      .subscribe({
        next: (appointments: IAppointment[]) => {
          const totalAppointments = appointments.length;
          const info = totalAppointments
            ? this.translate.instant(
                'ADMIN.PATIENTS.CONFIRM.DELETE.APPOINTMENTS',
                {
                  appointments: totalAppointments,
                },
              )
            : '';
          this.confirmationService.confirm({
            header: this.translate.instant(
              'ADMIN.PATIENTS.CONFIRM.DELETE.TITLE',
              {
                patient: patient.name,
              },
            ),
            message: `${this.translate.instant(
              'ADMIN.PATIENTS.CONFIRM.DELETE.TEXT',
              {
                patient: patient.name,
              },
            )} ${info}`,
            accept: () => this.removePatient(patient),
          });
        },
        error: (error) => this.showError(error),
      });
  }

  private removePatient(patient: IPatient): void {
    this.patientsService
      .delete(this.customers_id, this.sites_id, patient)
      .subscribe({
        next: () => {
          this.loadPatients();
          this.messageService.add({
            summary: this.translate.instant('MESSAGES.PATIENTS.DELETE.SUMMARY'),
            detail: this.translate.instant('MESSAGES.PATIENTS.DELETE.DETAIL', {
              patient: patient.name,
            }),
            severity: 'success',
          });
        },
        error: (error: HttpErrorResponse) => {
          const code = this.errorsService.getMessageFromPatientError(error);
          this.messageService.add({
            summary: this.translate.instant('MESSAGES.PATIENTS.ERROR.SUMMARY'),
            detail: this.translate.instant(`MESSAGES.PATIENTS.ERROR.${code}`),
            severity: 'error',
          });
        },
      });
  }

  private showError(error: any): void {
    const code = this.errorsService.getMessageFromPatientError(error);
    this.messageService.add({
      detail: this.translate.instant(`MESSAGES.PATIENT.ERROR.${code}`),
      summary: this.translate.instant('MESSAGES.PATIENT.ERROR.SUMMARY'),
      severity: 'error',
    });
  }
}
